import { api, LightningElement, wire } from "lwc";

import type {
  FavoriteMutationType,
  UnfavoriteMutationType,
} from "@devforce/trailhead-client-gql";
import { hasAccessTokenResolver } from "@devforce/trailhead-client-auth";
import { getEmitter } from "@devforce/trailhead-client-events";
import {
  FavoriteMutation,
  UnfavoriteMutation,
} from "@devforce/trailhead-client-gql";

import type { ContentSummary } from "common/contentSummary";
import {
  ActionConfig,
  ContentAction,
  getLearningActions,
} from "common/contentActions";
import { LabelsAdapter, LabelsAdapterValue } from "common/labelsAdapter";
import { memoizeOnce } from "common/memoize";
import {
  trackUserActivity,
  getUserActivityLearningActionPayload,
} from "common/track";

import { showToast } from "tds/toaster";
import type TdsActionsMenu from "tds/actionsMenu";

import * as locales from "./locales";

function isAuthenticated() {
  return hasAccessTokenResolver();
}

export default class extends LightningElement {
  @api config: ActionConfig = null!;
  @api summary: ContentSummary = null!;
  @api pageLocation: string | null = null;
  private contentType = "";

  connectedCallback() {
    if (this.summary && this.summary.type) {
      this.contentType = this.summary.type;
    }
  }

  private getMenuConfig = memoizeOnce(getLearningActions);
  private get menuConfig() {
    return this.getMenuConfig(this.config);
  }

  private get actionWithLabel(): ContentAction | null {
    let { menuConfig } = this;
    if (!menuConfig?.action) return null;
    return {
      ...menuConfig.action,
      label: this.labels[`action_${menuConfig.action.name}`],
    };
  }

  private get actionsWithLabel() {
    let { menuConfig } = this;
    if (!menuConfig?.actions) return null;
    return menuConfig.actions.map((section) => ({
      ...section,
      actions: section.actions.map((action) => ({
        ...action,
        label: this.labels[`action_${action.name}`] || action.label,
      })),
    }));
  }

  @wire(LabelsAdapter, { locales, options: "$labelOptions" })
  private labels: LabelsAdapterValue = {};

  private get labelOptions() {
    return {
      action_favorite_success: {
        url: "/users/favorites",
      },
      action_unfavorite_success: {
        url: "/users/favorites",
      },
    };
  }

  private detailsModal = false;
  private detailsModalConstructor: typeof LightningElement | null = null;

  private trailmixAddModal = false;
  private trailmixAddModalConstructor: typeof LightningElement | null = null;

  private handleAction(event: CustomEvent<ContentAction>) {
    const action = event.detail;
    if (action.authRequired && !isAuthenticated()) {
      getEmitter().emit("login");
      return;
    }
    let pageLocation = this.pageLocation ?? "th-content-action";
    let payload = {};
    switch (action.name) {
      case "favorite":
        trackUserActivity(
          this,
          "favorite_clicked",
          getUserActivityLearningActionPayload(this.config.learning, {
            pageLocation,
          })
        );

        payload = this.handleFavorite();
        this.refocusActionsMenu(event);
        return payload;
      case "unfavorite":
        trackUserActivity(
          this,
          "unfavorite_clicked",
          getUserActivityLearningActionPayload(this.config.learning, {
            pageLocation,
          })
        );

        payload = this.handleUnfavorite();
        this.refocusActionsMenu(event);
        return payload;
      case "trailmix_add":
        trackUserActivity(
          this,
          "add_to_trailmix_clicked",
          getUserActivityLearningActionPayload(this.config.learning, {
            pageLocation,
          })
        );
        return this.handleTrailmixAdd();
      case "details":
        trackUserActivity(
          this,
          "details_clicked",
          getUserActivityLearningActionPayload(this.config.learning, {
            pageLocation,
          })
        );
        this.dispatchEvent(
          new CustomEvent("trailhead_details_modal_opened", {
            bubbles: true,
            composed: true,
          })
        );
        return this.handleDetails();
      default:
        this.dispatchActionEvent(event.detail);
        break;
    }
  }

  private refocusActionsMenu(event: CustomEvent<ContentAction>) {
    (event.currentTarget as TdsActionsMenu)?.focus();
  }

  private async handleFavorite() {
    try {
      let { getClient, gql } = await import(
        "@devforce/trailhead-client-apollo"
      );
      let optimisticResponse: FavoriteMutationType = {
        markAsFavorite: {
          __typename: "MarkAsFavoritePayload",
          // @ts-ignore
          learning: {
            ...this.config.learning,
            favorite: true,
          },
        },
      };
      await getClient().mutate({
        mutation: gql`
          ${FavoriteMutation}
        `,
        variables: {
          name: this.config.learning.name,
          type: this.config.learning.type,
        },
        optimisticResponse,
      });
    } catch {
      showToast({
        message: this.labels.action_favorite_error,
        variant: "error",
      });
    }
  }

  private async handleUnfavorite() {
    try {
      let { getClient, gql } = await import(
        "@devforce/trailhead-client-apollo"
      );
      let optimisticResponse: UnfavoriteMutationType = {
        unfavorite: {
          __typename: "UnfavoritePayload",
          // @ts-ignore
          learning: {
            ...this.config.learning,
            favorite: false,
          },
        },
      };
      await getClient().mutate({
        mutation: gql`
          ${UnfavoriteMutation}
        `,
        variables: {
          name: this.config.learning.name,
          type: this.config.learning.type,
        },
        optimisticResponse,
      });
    } catch {
      showToast({
        message: this.labels.action_unfavorite_error,
        variant: "error",
      });
    }
  }

  private handleDetails() {
    import("th/detailsModal")
      .then((m) => {
        this.detailsModal = true;
        this.detailsModalConstructor = m.default;
      })
      .catch(console.log);
  }

  private handleTrailmixAdd() {
    import("th/trailmixAddModal")
      .then((m) => {
        this.trailmixAddModal = true;
        this.trailmixAddModalConstructor = m.default;
      })
      .catch(console.log);
  }

  private dispatchActionEvent(action: ContentAction) {
    this.dispatchEvent(new CustomEvent("action", { detail: action }));
  }

  private refocusTrigger() {
    (
      this.template
        .querySelector("*")
        ?.shadowRoot?.querySelector(".trigger") as HTMLElement
    )?.focus();
  }

  private handleDetailsModalClose() {
    this.dispatchEvent(
      new CustomEvent("trailhead_details_modal_closed", {
        bubbles: true,
        composed: true,
      })
    );
    this.detailsModal = false;
    this.refocusTrigger();
  }

  private handleTrailmixAddModalClose() {
    this.trailmixAddModal = false;
    this.refocusTrigger();
  }
}
